"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (t: Theme) => void;
  autoSwitched: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "bp-theme";
const VISITED_KEY = "bp-theme-visited";
const MD_BREAKPOINT = 768;

function getEnvDefault(): Theme {
  const raw = (process.env.NEXT_PUBLIC_DEFAULT_MODE ?? "").toLowerCase();
  if (raw === "light") return "light";
  if (raw === "dark") return "dark";
  return "system";
}

function isDesktopScreen(): boolean {
  if (typeof window === "undefined") return true;
  return window.innerWidth >= MD_BREAKPOINT;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyThemeClass(resolved: ResolvedTheme) {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

function enableTransition() {
  const root = document.documentElement;
  root.classList.add("theme-transition");
  const timeout = setTimeout(() => root.classList.remove("theme-transition"), 350);
  return () => clearTimeout(timeout);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("system");
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>("light");
  const [autoSwitched, setAutoSwitched] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const desktop = isDesktopScreen();

    if (!desktop) {
      setThemeState("light");
      setResolvedTheme("light");
      applyThemeClass("light");
      setMounted(true);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    const envDefault = getEnvDefault();
    const initial = stored && ["light", "dark", "system"].includes(stored) ? stored : envDefault;
    setThemeState(initial);

    const resolved = initial === "system" ? getSystemTheme() : initial;
    setResolvedTheme(resolved);
    applyThemeClass(resolved);
    setMounted(true);

    const isFirstVisit = !localStorage.getItem(VISITED_KEY);
    if (isFirstVisit) {
      localStorage.setItem(VISITED_KEY, "1");
      if (!stored) {
        const shouldGlow =
          envDefault === "dark" ||
          (envDefault === "system" && getSystemTheme() === "dark");
        if (shouldGlow) {
          const timer = setTimeout(() => {
            enableTransition();
            setResolvedTheme("dark");
            applyThemeClass("dark");
            setAutoSwitched(true);
            setTimeout(() => setAutoSwitched(false), 4000);
          }, 2000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if (theme === "system") {
        const sys = getSystemTheme();
        enableTransition();
        setResolvedTheme(sys);
        applyThemeClass(sys);
      }
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [theme, mounted]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
    enableTransition();
    const resolved = newTheme === "system" ? getSystemTheme() : newTheme;
    setResolvedTheme(resolved);
    applyThemeClass(resolved);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, autoSwitched }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
